<script setup lang="ts">
  import { ref } from "vue";
  import { storeToRefs } from "pinia";
  import _ from "lodash";
  import InfiniteLoading from "vue-infinite-loading";
  import { DynamicScroller, DynamicScrollerItem } from "~/customModules/vue-virtual-scroller.esm.js";
  import "~/customModules/vue-virtual-scroller.css";
  import { useTalkRoomStore } from "~/stores/TalkRoomStore";
  import { useTalkRoomTmpViewStore } from "~/stores/TalkRoomTmpViewStore";
  import { useTalkRoomViewStore } from "~/stores/TalkRoomViewStore";
  import { useLoginStore } from "~/stores/LoginStore";
  import { Talk, copyTalk } from "~/entities/Talk";
  import { TalkRoom } from "~/entities/TalkRoom";
  import { PlanInfo } from "~/entities/PlanInfo";
  import { useSnackBarStore } from "~/stores/SnackBarStore";
  import TalkMessage from "~/components/Organisms/TalkMessage.vue";
  import { TALK_DATE_FORMAT } from "~/consts/TalkConfig";

  const talkRoomStore = useTalkRoomStore();
  const talkRoomTmpViewStore = useTalkRoomTmpViewStore();
  const talkRoomViewStore = useTalkRoomViewStore();
  const loginStore = useLoginStore();
  const snackBarStore = useSnackBarStore();

  interface Props {
    planInfo: PlanInfo;
    selectedTalkRoom: TalkRoom;
    canAction: boolean;
    searchIdx: number | null;
    searchTalkId: string | null;
    searchWord: string | null;
    height: string;
    paddingBottom: string;
    scrollToBottomWaitIntervalMillis?: number;
    delayTalksObserveMillis?: number;
    isTest?: boolean; // テスト時のみ true にする
  }
  const props = withDefaults(defineProps<Props>(), {
    scrollToBottomWaitIntervalMillis: 50,
    delayTalksObserveMillis: 50,
    isTest: false,
  });

  interface Emits {
    (event: "isJumping", isJumping: boolean, talkId: string): void;
  }
  const emit = defineEmits<Emits>();

  watch(
    // searchIdx: 検索実行直後や前へ次への場合に検知 (searchTalkId はスレッド内でヒットする場合に変化しないので、インデックスを使う)
    // searchWord: キーワードが変わった場合に検知 (searchTalkId が変わらない場合でもアニメーションしたいので明示的に指定)
    () => [props.searchIdx, props.searchWord],
    async () => {
      if (props.searchTalkId) {
        // 検索ボックスの分だけ offset
        await jumpToTalk(props.searchTalkId, true, 56);
      }
    },
  );

  const planId = computed(() => props.planInfo.id);
  const talkRoomId = computed(() => props.selectedTalkRoom.talkRoomId);

  const talkMessageRefs = ref<Record<string, InstanceType<typeof TalkMessage>>>({});
  function setTalkMessageRef(id: string, el: any) {
    if (el) {
      talkMessageRefs.value[id] = el;
    }
  }

  // 権限周り制御
  const isOwner = computed<boolean>(() => props.planInfo.isOwner);
  const isOwnerOrMember = computed<boolean>(() => props.planInfo.isOwner || props.planInfo.isContract);

  // 講師IDリスト
  const ownerIds = computed<string[]>(() => {
    return getOwnerIds(props.planInfo);
  });

  // 閲覧権限がない場合はトップに戻す (TalkView でチェック済みのためありえないが、念のため)
  if (!canViewTalkRoom(props.selectedTalkRoom, isOwner.value, props.planInfo.isContract)) {
    reloadNuxtApp({ path: `/` });
  }

  // いいね権限
  const canLike = computed<boolean>(() => {
    // アーカイブ済みは投稿不可
    if (props.selectedTalkRoom.isArchive) {
      return false;
    }
    if (isOwner.value) return true;
    if (props.selectedTalkRoom.published) {
      if (loginStore.isLogin()) {
        // 契約関係なく、ログインしていればいいねはできる
        return true;
      }
      return false;
    } else {
      return false;
    }
  });

  // レスポンシブ対応
  const isTablet = isTabletAndSpDisplayRef();

  // 上下無限スクロール発生の都度カウントアップ
  const pageLoadCount = ref(1);

  // １度の通信でのトーク取得数。スマホはスクロールがかくつくので少なめにする
  // 上下無限スクロール発生の都度増加させるが、最大は 100 とする
  const pageSize = computed<number>(() => Math.min(100, (isTablet.value ? 20 : 40) * pageLoadCount.value));

  // 各エリアの高さ
  const messageAreaHeight = computed(() => props.height);
  const inputAreaHeight = computed(() => props.paddingBottom);

  // FIXME any 使いたくないが warning 消せないので・・
  // const scrollArea = ref<InstanceType<typeof DynamicScroller> | null>(null);
  const scrollArea = ref<InstanceType<any> | null>(null);

  const userProfileModal = ref<{ openModal: (userId: any) => void } | null>(null);
  const talkLikeDialog = ref<{ openModal: (talkId: string, goodCount: number) => void } | null>(null);

  // スクロール中に表示する日付
  const talkDate = ref<string>("");

  // スクロール中に表示する日付を更新する
  const updateTalkDate = () => {
    if (!scrollArea.value) {
      return;
    }
    // スクロール領域の位置を取得
    const scrollTop = scrollArea.value.$el.getBoundingClientRect().top;

    // ターゲット要素を取得
    const targets = Array.from(document.querySelectorAll(".vue-recycle-scroller__item-view"));
    let targetIndex = -1;
    // targets配列をtarget.getBoundingClientRect().bottomでソートする
    targets.sort((a, b) => {
      return a.getBoundingClientRect().bottom - b.getBoundingClientRect().bottom;
    });

    for (const target of targets) {
      // ターゲット要素の位置を取得
      const targetBottom = target.getBoundingClientRect().bottom;
      // ターゲットが表示されているか判定
      if (targetBottom >= scrollTop) {
        // ターゲットのインデックスを取得
        const child = target.children[0];
        if (child) {
          targetIndex = Number(child.getAttribute("data-index"));
        }
        break;
      }
    }
    if (targetIndex === -1) return;

    // 表示されているデータはstartIndex + targetIndex番目のデータ
    const visibleStartData = filterdTalks.value[targetIndex];
    if (!visibleStartData) return;
    if (visibleStartData.messageType === "date") {
      // 日付が表示されている場合は、日付固定表示はしない
      talkDate.value = "";
      return;
    }
    talkDate.value = FormatDateString(visibleStartData.postDateTime, TALK_DATE_FORMAT);
  };

  // チャットメッセージリスト。フィルタリングが必要なので保持しておく
  const filterdTalks = ref<Talk[]>([]);
  const updateFilterdTalks = () => {
    // アクティブなトークのみ表示用に抽出して日付を付与
    const filterdActiveTalks = activeTalks.value ? filterdTalk(activeTalks.value) : [];

    // TODO デバッグ用（あとで消す）。トークID順序が狂っていないかをチェック
    const checkTalkOrders = (talkList: Talk[], logPrefix: string) => {
      const talksWithoutDate = talkList.filter(talk => talk.messageType !== "date");
      for (let i = 0; i < talksWithoutDate.length - 1; i++) {
        // 昇順でなければエラーログを出力
        if (Number(talksWithoutDate[i].talkId) >= Number(talksWithoutDate[i + 1].talkId)) {
          consoleError(
            `${logPrefix}: roomID: ${props.selectedTalkRoom.talkRoomId}, talks: ${talksWithoutDate[i].talkId} -> ${talksWithoutDate[i + 1].talkId}`,
            talkList,
          );
          break;
        }
      }
    };
    // TODO デバッグ用（あとで消す）。各トークのユーザー情報を取得済みかチェック
    const checkTalkUsers = (talkList: Talk[], logPrefix: string) => {
      // production はログが多すぎるので除外
      if (useRuntimeConfig().public.appEnv === "production") {
        return;
      }

      const talksWithoutDate = talkList.filter(talk => talk.messageType !== "date");
      for (let i = 0; i < talksWithoutDate.length; i++) {
        const talk = talksWithoutDate[i];
        if (!(talk.userId in talkRoomStore.talkRoomUsers)) {
          sentryErrorLog(`${logPrefix}: user not found`, talk);
        }
      }
    };

    // TODO デバッグ用（あとで消す）。表示用トークリスト設定前チェック
    checkTalkOrders(filterdActiveTalks, "updateFilterdTalks order wrong 1 before");
    checkTalkUsers(filterdActiveTalks, "[FINCS-2653] updateFilterdTalks user missing 1 before");

    // 表示用トークリスト filterdTalks.value を条件に応じて更新
    if (filterdActiveTalks.length !== filterdTalks.value.length) {
      // トークリストの長さが変わったらすべて置き換える
      filterdTalks.value = filterdActiveTalks;
    } else {
      // トークリストの長さが同じ場合はトーク個別に比較して更新
      for (let i = 0; i < filterdActiveTalks.length; i++) {
        if (filterdActiveTalks[i].talkId !== filterdTalks.value[i].talkId) {
          // トークIDが変わっていればトーク単位で置き換える
          filterdTalks.value[i] = filterdActiveTalks[i];
        } else {
          // トークIDが同じなら可変要素のみコピー
          copyTalk(filterdActiveTalks[i], filterdTalks.value[i]);
        }
      }
    }

    // TODO デバッグ用（あとで消す）。表示用トークリスト設定後チェック
    checkTalkOrders(filterdTalks.value, "updateFilterdTalks order wrong 2 after");
    checkTalkUsers(filterdTalks.value, "[FINCS-2653] updateFilterdTalks user missing 2 after");

    // デバッグ用。表示更新の都度トークリストの内容をログ出力
    // showConsoleLog(`talks count=${filterdTalks.value.length}`, filterdTalks);

    // 既読処理（末尾トークIDがある場合のみ）
    if (lastTalkId.value) {
      processLastRead(talkRoomId.value, lastTalkId.value);
    }

    // トーク監視範囲を調整
    // 即時実行するとスレッド子追加＆スレッド親更新イベント同時発生時に取りこぼすので、少しだけ遅延実行させる
    setTimeout(startTalksObserve, props.delayTalksObserveMillis);
  };

  // 画像メッセージリスト。前後移動のために保持しておく
  const filteredImageTalks = computed<Talk[]>(() => {
    return filterdTalks.value.filter(talk => {
      return talk.messageType === "image";
    });
  });

  // トークリストをフィルタリングする
  // - 日付をトーク内に入れる
  // - トーク画像コンテンツ・サムネイルURL調整
  const filterdTalk = (activeTalks: Talk[]): Talk[] => {
    // 最初のトーク判定用のフラグ
    // talkRoomStore.hasPrevTalk だけだと1画面以下のメッセージ数の場合に判定できない（上無限スクロールが発生しない）ので、
    // TalkRepository.getTalks の再読み込み基準である 10 個未満であれば最初とみなす
    const noPreviousTalk = !talkRoomStore.hasPrevTalk || activeTalks.length < 10;

    return insertDatesToTalks(activeTalks, noPreviousTalk, isOwnerOrMember.value);
  };

  // InfiniteLoading 用の identifier
  const identifierTop = ref(1);
  const identifierBottom = ref(1);

  const initTalks = async () => {
    identifierTop.value += 1;
    identifierBottom.value += 1;
    pageLoadCount.value = 1;
    talkRoomStore.initTalkRoom(talkRoomId.value);
    filterdTalks.value = [];

    // 未読・既読関連
    lastReadTalkId.value = undefined;
    initialReadTalkId.value = undefined;
    canShowUnreadTalks.value = false;
    unreadTalks.value = "";

    loadingTalks.value = false;
    showLoadingTalksTop.value = false;
    setScrollTop(0);
    initScroll = false;
    hasJumped.value = false;

    showLoadingTalksTop.value = true;
    await getTalks(true);

    // talks 初期化後に監視スタート
    startTalksObserve();
    talkRoomStore.startUserGoodsObserve(talkRoomId.value);
  };

  const finishTalks = () => {
    talkRoomStore.stopTalksObserve();
    talkRoomStore.stopUserGoodsObserve();
  };

  // データ取得中のフラグ。通信のみ
  const loadingTalks = ref(false);
  // データ取得ローディング表示フラグ。スクロール判定中も表示する
  const showLoadingTalksTop = ref(false);
  const showLoadingTalksBottom = ref(false);
  const showLoadingTalks = computed(() => showLoadingTalksTop.value || showLoadingTalksBottom.value);

  // データ取得中・スクロール中・ジャンプ中のいずれか
  const isProcessing = computed(() => loadingTalks.value || showLoadingTalks.value || isJumping.value);

  // 先頭・末尾のトークID取得
  const firstTalkId = computed<string | undefined>(() => {
    return talkRoomStore.talks.at(0)?.talkId;
  });
  const lastTalkId = computed<string | undefined>(() => {
    return talkRoomStore.talks.at(-1)?.talkId;
  });

  // アクティブ（表示可能）トークリスト
  const activeTalks = computed<Talk[]>(() => {
    return _.filter(talkRoomStore.talks, tk => {
      return tk.canView();
    });
  });
  const lastActiveTalkId = computed<string | undefined>(() => {
    return activeTalks.value?.at(-1)?.talkId;
  });

  const useCacheOnGetTalks = computed<boolean>(() => {
    // ジャンプ実績がある場合はキャッシュを使わない
    // トークが大量にある場合にジャンプでキャッシュアクセスすると重くなってしまうため
    // あとは store の設定に依存
    return !hasJumped.value && talkRoomViewStore.isFirestoreCacheEnabled;
  });

  // データ取得
  const getTalks = async (isBefore: boolean, wait = false) => {
    if (loadingTalks.value) return;
    loadingTalks.value = true;
    if (wait) {
      // Safari の場合、ウェイトを入れないとスクロールがガタガタになるので調整する (Chrome, Edge は問題なし)
      if (isSafari()) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }
    try {
      if (isBefore) {
        await talkRoomStore.getTalksBefore(
          planId.value,
          talkRoomId.value,
          firstTalkId.value,
          pageSize.value,
          useCacheOnGetTalks.value,
        );
      } else {
        await talkRoomStore.getTalksAfter(
          planId.value,
          talkRoomId.value,
          lastTalkId.value,
          pageSize.value,
          useCacheOnGetTalks.value,
        );
      }
      pageLoadCount.value += 1;
    } finally {
      if (isBefore) {
        showLoadingTalksTop.value = false;
      } else {
        showLoadingTalksBottom.value = false;
      }
      loadingTalks.value = false;
    }
  };

  // 特定トークの存在チェック
  const hasTalk = async (talkId: string) => {
    return await talkRoomStore.hasTalk(planId.value, talkRoomId.value, talkId);
  };

  // 特定トーク前後のデータ取得
  const getTalksAt = async (talkId: string) => {
    // ジャンプ先を基準とするため初期化
    pageLoadCount.value = 1;

    // 対象トーク付近のリスト(前後10-20件)を取得
    // 既存のデータはクリアされ、以降は上下無限スクロール可能な状態となる
    await talkRoomStore.getTalksAt(planId.value, talkRoomId.value, talkId, pageSize.value, useCacheOnGetTalks.value);
  };

  const getTalkElement = (talkId: string) => {
    const targetTalks = document.querySelectorAll(`[data-talkid="${talkId}"]`);
    if (targetTalks.length === 0) {
      return null;
    }
    return targetTalks[0];
  };

  // 対象トークが表示されているか判定
  const isTalkInView = (talkId: string, offset: number) => {
    const scrollAreaRect = scrollArea.value?.$el.getBoundingClientRect();
    if (!scrollAreaRect) {
      return false;
    }
    const targetTalkRect = getTalkElement(talkId)?.getBoundingClientRect();
    if (!targetTalkRect) {
      return false;
    }
    if (targetTalkRect.top < scrollAreaRect.top + offset) {
      // トークの上端がスクロールエリアの上端より上にある場合は、上にはみ出しているので false 判定
      // ただしチャンネル内の最初のメッセージだけ特例で、トークの上端がスクロールエリアの上端付近（offset 以内）にありスクロールが上に張り付いている場合のみ true 判定
      return targetTalkRect.top >= scrollAreaRect.top && scrollArea.value?.$el.scrollTop === 0;
    } else {
      // https://fincs.backlog.com/view/FINCS-2608 対策
      // scrollAreaRect.bottom に足している 40 は、実際より bottom が 35 px 小さくなっていたためにスクロールできないケースがあることが観測されたため（苦肉の策）
      // この 40 がないと、末尾のトークが見えているのに見えていないと判定され、返信ジャンプが上手くいかない
      // 上記チケットのように末尾のすぐ上のトークが大きくて画面上部にはみ出している場合に問題となっていた
      if (targetTalkRect.bottom <= scrollAreaRect.bottom + 40) {
        // ここでトークの下端がスクロールエリアの下端より上にある場合は確実に収まる
        return true;
      }
      // トークのサイズが大きく画面が小さい場合にここへ到達
      // トークの上端がスクロールエリアの上端 SCROLL_INTERVAL_HEIGHT 以内にある場合は ok とする
      return targetTalkRect.top < scrollAreaRect.top + offset + SCROLL_INTERVAL_HEIGHT;
    }
  };

  const talkFoundAnimation = (talkId: string) => {
    if (!talkMessageRefs.value) {
      return;
    }

    const talkMessage = talkMessageRefs.value[talkId] as InstanceType<typeof TalkMessage>;
    if (talkMessage) {
      // トークメッセージのアニメーション
      (talkMessage as InstanceType<typeof TalkMessage>).animateFound();
    }
  };

  // 返信元までスクロール
  const scrollToReply = (targetTalkId: string) => {
    jumpToTalk(targetTalkId);
  };

  // 対象トークへ到達するためにスクロールアップすべきか判定
  const shouldScrollUp = (targetTalkId: string, offset: number) => {
    for (const talk of activeTalks.value) {
      if (isTalkInView(talk.talkId, offset)) {
        // 一番上に表示されているトークよりも ID が小さければスクロールアップすべき
        return Number(targetTalkId) < Number(talk.talkId);
      }
    }
    return false;
  };

  // 特定のトークまでジャンプ中か否か
  const isJumping = ref(false);

  // 特定のトークまでジャンプしたことがあるか
  const hasJumped = ref(false);

  // 特定のトークまでジャンプ
  // - ジャンプ後に offset 分だけ下にずらして表示
  // - toNextTalk が true の場合、talkId の次のトークを対象にジャンプさせる
  const jumpToTalk = async (talkId: string, animate = true, offset: number = 0, toNextTalk = false) => {
    // 次トークが対象の場合の調整
    const adjustForNextTalk = () => {
      if (toNextTalk) {
        // activeTalks にて talkId の次のトークを取得してすり替える
        const talkIndex = activeTalks.value.findIndex(talk => Number(talk.talkId) === Number(talkId));
        if (talkIndex !== -1 && talkIndex + 1 < activeTalks.value.length) {
          talkId = activeTalks.value[talkIndex + 1].talkId;
          toNextTalk = false;
        }
      }
    };

    isJumping.value = true;
    emit("isJumping", true, talkId);
    try {
      adjustForNextTalk();
      if (!toNextTalk) {
        // 対象トーク確定時のみ
        // 対象トークが DOM にあれば即移動して終わり
        if (scrollToTalkInDom(talkId, animate, offset)) {
          return;
        }
      }

      // 対象トーク存在チェック
      if (!(await hasTalk(talkId))) {
        // 対象トークが取得できない場合はエラー終了。削除済みや存在しないトーク指定時など
        snackBarStore.setError("メッセージが見つかりません");
        return;
      }

      // ジャンプ実績ありとして、以降キャッシュを無効化する
      // firestore キャッシュのクエリ速度はキャッシュ済みデータ量に依存するため、
      // ルームにトークが大量にあるときにジャンプでキャッシュアクセスすると重くなってしまうので
      hasJumped.value = true;

      // 対象トーク付近のリスト(前後10-20件)を取得
      // 既存のデータはクリアされ、以降は上下無限スクロール可能な状態となる
      await getTalksAt(talkId);

      adjustForNextTalk();
      if (toNextTalk) {
        if (talkId === "0") {
          // すべて未読の場合に先頭の未読へジャンプする場合、先頭アクティブトークへジャンプさせる
          talkId = firstTalkId.value!;
        } else {
          // 対象トーク未確定はありえない
          sentryErrorLog(`jumpToTalk: toNextTalk=true, room=${talkRoomId.value}, talkId=${talkId}`);
          return;
        }
      }

      // ローダーを初期化して上下無限スクロールに備える
      identifierTop.value += 1;
      identifierBottom.value += 1;

      // この時点で対象トークが DOM にあれば即移動して終わり
      if (scrollToTalkInDom(talkId, animate, offset)) {
        return;
      }

      // 対象トークまでスクロール
      // 通信は発生しないので timeout 5 秒で十分
      await scrollUntil(talkId, animate, offset, 5000);
    } catch (error: any) {
      // 一定数発生するが、ユーザー指摘もないのでひとまず warn ログとする
      sentryWarnLog(`TalkRoomDetailMessages.jumpToTalk: ${error}`);
    } finally {
      isJumping.value = false;
      emit("isJumping", false, talkId);
    }
  };

  const openThread = (threadTalkId: string, targetTalkId: string | undefined) => {
    if (showThread.value) {
      // すでにスレッド表示中の場合は処理しない。
      // 表示中ルームと別のルームのブックマーク・メンションへ飛ぶ場合などに発生するが、
      // 処理競合でエラーとなってしまうのでガード。
      return;
    }

    threadTalk.value = activeTalks.value.find(talk => talk.talkId === threadTalkId);
    if (!threadTalk.value) {
      // スレッドトークが取得できない場合はエラー終了。削除済みや存在しないトーク指定時など
      snackBarStore.setError("メッセージが見つかりません");
      return;
    }
    threadTargetTalkId.value = targetTalkId;
    showThread.value = true;
  };

  // 特定のトークまでスクロール
  const SCROLL_INTERVAL_HEIGHT = 200;
  const scrollUntil = (targetTalkId: string, animate: boolean, offset: number, timeout: number): Promise<void> => {
    // スクロールエリアがない場合はスクロールしない
    if (!scrollArea.value) {
      return Promise.resolve();
    }
    const scrollAreaRect = scrollArea.value.$el.getBoundingClientRect();

    // スクロールする方向を決定
    const goingUp = shouldScrollUp(targetTalkId, offset);

    // スクロール量と間隔
    const scrollIntervalHeight = goingUp ? SCROLL_INTERVAL_HEIGHT : -SCROLL_INTERVAL_HEIGHT;
    const scrollIntervalMillis = 10;

    // 一度スクロールし、指定時間後にコールバックする
    const scrollOnce = (height: number, callback: () => void) => {
      requestAnimationFrame(() => {
        moveUpScrollTop(height);
        setTimeout(callback, scrollIntervalMillis);
      });
    };

    // targetTalkId に到達するまでスクロール
    return new Promise<void>((resolve, reject) => {
      // timeout までに完了しない場合はエラーとする
      let wasError = false;
      const errorTimer = setTimeout(() => {
        wasError = true;
        reject(new Error(`scrollOnce: timeout ${timeout} ms`));
      }, timeout);

      const scrollIfNeeded = () => {
        // エラー発生したら処理中断
        if (wasError) {
          return;
        }
        // ローディング中はスクロールしないで次のタイマーだけ仕掛ける
        if (showLoadingTalks.value) {
          setTimeout(scrollIfNeeded, scrollIntervalMillis);
          return;
        }
        // 対象トークが DOM にあれば即移動して終わり
        if (scrollToTalkInDom(targetTalkId, animate, offset)) {
          resolve();
          return;
        }

        // targetTalkId が存在かつそれがスクロールエリア内に入るまで上スクロールする
        const targetTalk = getTalkElement(targetTalkId);
        if (!targetTalk) {
          // targetTalk がなければ scrollIntervalHeight だけスクロール
          scrollOnce(scrollIntervalHeight, scrollIfNeeded);
          return;
        }
        if (!isTalkInView(targetTalkId, offset)) {
          // targetTalk が表示されていなければ必要な分だけスクロールする
          const targetTalkRect = targetTalk.getBoundingClientRect();
          scrollOnce(Math.min(scrollIntervalHeight, scrollAreaRect.y + offset - targetTalkRect.y), scrollIfNeeded);
          return;
        }

        // 無事到達したのでエラータイマーをクリア
        clearTimeout(errorTimer);

        if (offset) {
          // 適宜オフセット調整
          moveUpScrollTop(offset);
        }

        // 到達後に必要ならアニメーション
        if (animate) {
          talkFoundAnimation(targetTalkId);
        }

        // スクロール完了
        resolve();
      };
      scrollIfNeeded();
    });
  };

  // DOM 内の特定のトークまでスクロール
  const scrollToTalkInDom = (talkId: string, animate: boolean, offset: number) => {
    const targetTalk = getTalkElement(talkId);
    if (!targetTalk) {
      // 対象トークが DOM にない場合は何もしない
      return false;
    }
    if (targetTalk.getBoundingClientRect().top < 0) {
      // 対象トークが スクロール範囲内 にない場合は何もしない (DynamicScroller の仕様か、top が -9000 などになる場合あり)
      return false;
    }
    requestAnimationFrame(() => {
      targetTalk.scrollIntoView({ behavior: "instant" });
      if (offset) {
        moveUpScrollTop(offset);
      }
      if (animate) {
        talkFoundAnimation(talkId);
      }
    });
    return true;
  };

  const showJumpToLastTalk = ref(false);
  const updateJumpToLastTalk = () => {
    showJumpToLastTalk.value = !isScrollAtBottom();
  };
  const onJumpToLastTalk = () => {
    if (!lastActiveTalkId.value) {
      return; // トーク未取得の場合は何もしない
    }

    if (talkRoomStore.hasNextTalk) {
      // 下無限スクロール可能な場合は、初期状態に戻すことで最新表示
      initTalks();
    } else {
      // 下無限スクロール不可な場合は、すでに最新トークを読み込み済みなため bottom スクロールで最新表示
      scrollToBottom();
    }
  };

  let talksLoadingStart = Date.now();
  const onScroll = () => {
    // スクロール中に表示する未読トーク数を更新する
    updateUnreadTalks();
    // スクロール中に表示する日付を更新する
    updateTalkDate();
    // 最新トークへのジャンプボタンを更新する
    updateJumpToLastTalk();

    // scrollendイベントはsafariが対応してないので、スクロール中フラグを立てて、setTimeoutでスクロール終了を判別する
    // ref: https://developer.mozilla.org/ja/docs/Web/API/Element/scrollend_event
    isScrolling.value = true;
    clearTimeout(scrollEndTid.value);
    scrollEndTid.value = setTimeout(() => {
      isScrolling.value = false;
      // Safari の場合はデータ挿入処理をスクロールが完全に止まってから処理しないとずれるため、 onScroll にてスクロール終了後に処理する
      if (isSafari()) {
        if (handleStateTop.value) {
          getTalksFromScrollTop(handleStateTop.value).finally(() => {
            showConsoleLog(`talksLoading: ${Date.now() - talksLoadingStart}`);
            handleStateTop.value = null;
          });
        } else if (handleStateBottom.value) {
          getTalksFromScrollBottom(handleStateBottom.value).finally(() => {
            showConsoleLog(`talksLoading: ${Date.now() - talksLoadingStart}`);
            handleStateBottom.value = null;
          });
        }
      }
    }, 200);
  };
  // スクロール位置が最上部まで来た時のイベント
  const onScrollTop = ($state: any) => {
    if (!talkRoomStore.hasPrevTalk || loadingTalks.value || showLoadingTalksTop.value) {
      return;
    }
    talksLoadingStart = Date.now();
    showLoadingTalksTop.value = true;
    handleStateTop.value = $state;
    // 上スクロールのためのトーク読み込み
    // Safari の場合はデータ挿入処理をスクロールが完全に止まってから処理しないとずれるため、 onScroll にてスクロール終了後に処理する
    if (isSafari()) {
      scroll();
    } else {
      getTalksFromScrollTop(handleStateTop.value).finally(() => {
        showConsoleLog(`talksLoading: ${Date.now() - talksLoadingStart}`);
        handleStateTop.value = null;
      });
    }
  };
  // スクロール位置が最下部まで来た時のイベント
  const onScrollBottom = ($state: any) => {
    if (!talkRoomStore.hasNextTalk || loadingTalks.value || showLoadingTalksBottom.value) {
      return;
    }
    showLoadingTalksBottom.value = true;
    handleStateBottom.value = $state;
    // 下スクロールのためのトーク読み込み
    // Safari の場合はデータ挿入処理をスクロールが完全に止まってから処理しないとずれるため、 onScroll にてスクロール終了後に処理する
    if (isSafari()) {
      scroll();
    } else {
      getTalksFromScrollBottom(handleStateBottom.value).finally(() => {
        showConsoleLog(`talksLoading: ${Date.now() - talksLoadingStart}`);
        handleStateBottom.value = null;
      });
    }
  };

  const getTalksFromScrollTop = async (state: any) => {
    await getTalks(true, true);
    if (talkRoomStore.hasPrevTalk) {
      state.loaded();
    } else {
      state.complete();
      // 反対側の infinite loader をリセットする。そうしないとなぜか反対側の無限スクロールが動かなくなる (一緒に complete に落ちている？？)
      identifierBottom.value += 1;
    }
  };

  const getTalksFromScrollBottom = async (state: any) => {
    await getTalks(false, true);
    if (talkRoomStore.hasNextTalk) {
      state.loaded();
    } else {
      state.complete();
      // 反対側の infinite loader をリセットする。そうしないとなぜか反対側の無限スクロールが動かなくなる (一緒に complete に落ちている？？)
      identifierTop.value += 1;
    }
  };

  // iosの慣性スクロールを回避する方法
  // ref: https://qiita.com/kilvis/items/0fcb240d634b3a8639ae
  const scrollEndTid = ref<any | null>(null);
  const handleStateTop = ref<any | null>(null);
  const handleStateBottom = ref<any | null>(null);
  // スクロール中かどうかのフラグ
  const isScrolling = ref(false);

  onMounted(async () => {
    await initTalks();
    if (scrollArea.value?.$el) {
      scrollArea.value.$el.addEventListener("scroll", onScroll);
    }
  });

  onUnmounted(() => {
    finishTalks();
    if (scrollArea.value?.$el) {
      scrollArea.value.$el.removeEventListener("scroll", onScroll);
    }
  });

  // ルーム切替時にトークルームを初期化
  watch(() => props.selectedTalkRoom, initTalks);

  watch(
    () => {
      return scrollArea.value?.$el;
    },
    () => {
      if (scrollArea.value?.$el) {
        scrollArea.value.$el.addEventListener("scroll", onScroll);
      }
    },
  );

  watch(
    () => talkRoomStore.updateTalk,
    updated => {
      if (!updated) {
        return;
      }
      try {
        // updateTalk が true へ変化時に表示するトークを更新
        updateFilterdTalks();
      } finally {
        // アップデートフラグをクリア
        talkRoomStore.clearUpdateTalkFlag(false);
      }
    },
  );

  // 既読処理済みトークID
  const lastReadTalkId = ref<string | undefined>();
  // 初期既読トークID
  const initialReadTalkId = ref<number | undefined>();
  // 先頭未読トークID
  const firstUnreadTalkId = computed<number | undefined>(() => {
    if (initialReadTalkId.value === undefined) {
      return undefined;
    }
    if (!loginStore.isLogin()) {
      // 未ログイン時は未読表示しない
      return undefined;
    }
    for (const talk of activeTalks.value) {
      if (talk.userId === loginStore.loginInfo!.userId) {
        // 自分の投稿は未読にしない
        continue;
      }
      if (Number(talk.talkId) > initialReadTalkId.value) {
        return Number(talk.talkId);
      }
    }
    return undefined;
  });
  // 未読トーク数表示可否
  const canShowUnreadTalks = ref<boolean>();
  // 未読トーク数表示文字列
  const unreadTalks = ref<string>("");
  // 未読ライン参照
  const unreadLineRef = ref<HTMLElement | null>(null);
  // スクロール中に表示する未読トーク数を更新する
  const updateUnreadTalks = () => {
    if (!scrollArea.value || !unreadLineRef.value) {
      return;
    }
    const scrollAreaRect = scrollArea.value.$el.getBoundingClientRect();
    const unreadLineRect = unreadLineRef.value.getBoundingClientRect();
    if (scrollAreaRect.top < unreadLineRect.top) {
      // 未読ラインより上にスクロールしたら未読トーク数表示を非表示にする
      canShowUnreadTalks.value = false;
    }
  };
  // 未読ライン表示可否
  const canShowUnreadLine = (talk: Talk) => {
    // 先頭未読トークIDと一致する場合
    return talk.talkId === firstUnreadTalkId.value?.toString();
  };
  // 初期既読トークID取得処理
  const fetchInitialReadTalkId = async () => {
    if (!loginStore.isLogin()) {
      // 未ログイン時は fetch しない
      return;
    }
    const currentLastReadTalkId = await talkRoomStore.getLastRead(loginStore.loginInfo!.userId, talkRoomId.value);
    if (currentLastReadTalkId) {
      initialReadTalkId.value = currentLastReadTalkId;
    } else {
      // undefined が返ってきたら既読トークIDが存在しないので全トークが未読
      // そのまま設定すると未 fetch と区別がつかないので、0 することで全トークを未読と判断させる
      initialReadTalkId.value = 0;
    }
  };
  // 初期既読トークID設定
  const setInitialReadTalkId = async () => {
    // 初期既読トークID未取得の場合のみ取得
    if (initialReadTalkId.value !== undefined) {
      return;
    }

    // 再入しないよう fetch 前に無限大に初期化 (最新トークより小さい値だと未読表示がちらつくので無限大とした)
    initialReadTalkId.value = Number.POSITIVE_INFINITY;

    // 初期既読トークID取得
    await fetchInitialReadTalkId();

    // ここから未読件数表示可能とする
    canShowUnreadTalks.value = true;
  };
  // 未読トーク数表示文字列設定
  const setUnreadTalks = (talkIdOfInitialRead: number) => {
    if (!loginStore.isLogin()) {
      // 未ログイン時は未読トーク数表示しない
      return;
    }
    // 未読件数取得
    let unreadCount = 0;
    for (const talk of [...activeTalks.value].reverse()) {
      if (Number(talk.talkId) <= talkIdOfInitialRead) {
        break;
      }
      if (loginStore.loginInfo!.userId === talk.userId) {
        // 自分の投稿は未読にしない
        continue;
      }
      unreadCount++;
    }
    if (unreadCount === 0) {
      // 未読なし
      unreadTalks.value = "";
    } else if (unreadCount >= activeTalks.value.length) {
      // 未読あり、件数未確定
      unreadTalks.value = `${unreadCount}+ 件未読`;
    } else {
      // 未読あり、件数確定
      unreadTalks.value = `${unreadCount} 件未読`;
    }
  };
  // 既読処理
  const processLastRead = async (roomId: string, talkId: string) => {
    // 以下の一連処理は順番に依存しているため、順番を変えないこと

    // 末尾のトークを必要に応じて既読処理する
    // 次の await setInitialReadTalkId() でコンテキストスイッチが起きる前に lastReadTalkId を更新し、
    // startTalksObserve で lastReadTalkId を読みとれるようにするため最初に実行
    const hasSetLastRead = setLastReadIfNeeded(talkId);

    // 初期既読トークID設定
    await setInitialReadTalkId();

    // 未読トーク数表示文字列設定
    // 上記 setInitialReadTalkId で設定した initialReadTalkId を使うため、その後に実行
    setUnreadTalks(initialReadTalkId.value!);

    // 末尾のトークを必要に応じて既読保存する
    // 上記 setInitialReadTalkId 内で talkRoomStore.getLastRead するので、talkRoomStore.setLastRead はその後に実行
    if (hasSetLastRead && loginStore.isLogin()) {
      // ログイン中のみ既読トークIDを保存
      await talkRoomStore.setLastRead(loginStore.loginInfo?.userId, roomId, talkId);
    }
  };
  // 未読トーク数表示クリック時処理
  const clickUnreadTalks = async () => {
    // 未読トーク数表示をクリア
    closeUnreadTalks();
    // 先頭未読トークへジャンプ
    // 未読ラインが見えるようオフセットを設定
    await jumpToTalk(initialReadTalkId.value!.toString(), true, 32, true);
  };
  // 未読トーク数表示を閉じる
  const closeUnreadTalks = () => {
    unreadTalks.value = "";
    canShowUnreadTalks.value = false;
  };
  // 末尾のトークを必要に応じて既読処理する
  const setLastReadIfNeeded = (talkId: string) => {
    if (!talkId) {
      return false; // トーク未取得
    }
    if (lastReadTalkId.value && Number(talkId) <= Number(lastReadTalkId.value)) {
      return false; // このトークIDは既読設定済み
    }
    // 既読処理 (論理削除済みを含めた最新のトークを既読する)
    lastReadTalkId.value = talkId;
    return true;
  };

  // トーク監視を開始
  const startTalksObserve = () => {
    // 監視を開始 (監視済みの場合もstop後にstartされるので問題なし)
    talkRoomStore.startTalksObserve(
      planId.value,
      talkRoomId.value,
      lastReadTalkId.value === lastTalkId.value,
      lastReadTalkId.value,
      firstTalkId.value,
      lastTalkId.value,
    );
  };

  let initScroll = false;
  let scrollingToBottom = false;

  // スクロールが最下部付近であるか判定
  const isScrollAtBottom = () => {
    if (!scrollArea.value) {
      return false;
    }
    const { scrollTop, offsetHeight, scrollHeight } = scrollArea.value.$el;
    // 100pxほどマージンを持たせて判別する。
    return scrollTop + offsetHeight >= scrollHeight - 100;
  };

  // 自分以外の投稿があった場合の処理
  // ・最下部付近にいる時に投稿があった場合は最新の投稿を表示する
  // ・過去ログを見てる時には最新位置にスクロールしない
  watch(
    () => {
      return filterdTalks.value?.length;
    },
    () => {
      // 末尾が直近の投稿でない場合はスクロールしない（下無限スクロールと区別がつかないため）
      if (talkRoomStore.hasNextTalk) {
        return;
      }

      // スクロール位置が最下部付近であれば最新を表示させる
      if (isScrollAtBottom()) {
        scrollToBottom();
      }
    },
  );

  // 初回読み込み時に最下部に移動する
  watch(
    () => {
      return filterdTalks.value?.length;
    },
    () => {
      if (scrollArea.value) {
        if (!initScroll) {
          scrollToBottom();
        }
        initScroll = true;
      }
    },
    { flush: "post" }, // このオプション指定でvueコンポーネントが更新された後に呼ばれる
  );

  const scrollToBottom = () => {
    if (!scrollingToBottom) {
      scrollingToBottom = true;
      scrollToPosition(999999999);
    }
  };

  const isTalkLoadingAsync = (talk: Talk) => {
    // OGP や返信元トークは、非同期で読み込むことあり
    return talk.ogpLoading || talk.replyTalk?.messageType === "loading";
  };

  const { initRenderingFinished, canSearch, showThread, threadTalk, threadTargetTalkId } =
    storeToRefs(talkRoomTmpViewStore);

  // 初回データクエリ → スクロール完了後にメッセージを表示するかを制御
  const SHOW_MESSAGE_INIT = true; // false だとかくつき予防できるが重く感じるので、デフォルト true にしておく
  const canShowMessages = computed(() => SHOW_MESSAGE_INIT || initRenderingFinished.value);

  const route = useRoute();

  // クエリパラメータで指定されたトーク・スレッドへジャンプする処理
  const jumpToTalkByQueryParams = async () => {
    if (route.query.room === props.selectedTalkRoom.talkRoomId && (route.query.talk || route.query.thread)) {
      try {
        if (route.query.thread) {
          // 対象トークのスレッド元トークへジャンプ
          await jumpToTalk(route.query.thread as string, false);
          openThread(route.query.thread as string, route.query.talk as string | undefined);
        } else {
          // 対象トークへジャンプ
          await jumpToTalk(route.query.talk as string);
        }
      } finally {
        // ジャンプ後に talk,thread クエリパラメータを削除。こうしないと画面遷移時に予期せぬジャンプが発生する場合があるため
        delete route.query.talk;
        delete route.query.thread;
      }
    }
  };
  // クエリパラメータで指定されたトーク・スレッドへジャンプを実行（同ルーム選択時）
  watch(() => [route.query.talk, route.query.thread], jumpToTalkByQueryParams);

  const scrollToPosition = (position: number) => {
    if (scrollArea.value) {
      setScrollTop(position);
      // アイテムの高さが可変のスクローラーなので一回の変更では上手く移動できない。
      // そのための苦肉の策として何度もスクロール位置変更処理をしている。
      // 2024-05-07 現在の実績ベースで 50msインターバル x 10回に設定
      // 2024-09-22 読込中のOGP・返信がある場合はスクロール継続
      requestAnimationFrame(() => {
        if (scrollArea.value) {
          setScrollTop(position);
          let scrollCount = 0;
          const tm = setInterval(() => {
            setScrollTop(position);
            // 非同期読み込みが走っているトークがある場合はスクロール継続
            const needToWaitMore = talkRoomStore.talks.some(t => isTalkLoadingAsync(t));
            if (needToWaitMore) {
              return;
            }
            scrollCount++;
            if (scrollCount === 10) {
              clearInterval(tm);
              scrollingToBottom = false;
              // 初期表示完了
              initRenderingFinished.value = true;
              // このタイミングで検索も可能とさせる
              canSearch.value = true;
              // このタイミングでクエリパラメータで指定されたトークへジャンプを実行（別ルーム選択時）
              jumpToTalkByQueryParams();
            }
          }, props.scrollToBottomWaitIntervalMillis);
        } else {
          scrollingToBottom = false;
        }
      });
    } else {
      scrollingToBottom = false;
    }
  };

  const setScrollTop = (position: number) => {
    // クラッシュ防止の為、scrollArea未設定の場合は処理をしない
    if (scrollArea.value) {
      scrollArea.value.$el.scrollTop = position;
    }
  };

  const moveUpScrollTop = (offset: number) => {
    // クラッシュ防止の為、scrollArea未設定の場合は処理をしない
    if (scrollArea.value) {
      scrollArea.value.$el.scrollTop = Math.max(0, scrollArea.value.$el.scrollTop - offset);
    }
  };

  const showConsoleLog = (message: string, ...data: any[]) => {
    consoleLog(`[DEBUG] ${message}`, ...data);
  };

  defineExpose({
    scrollToBottom, // トーク入力時に最下部にスクロールさせるため
  });
</script>

<template>
  <AtomsOverlay v-model:loading="isProcessing" scrim="transparent" />

  <!-- canShowMessages: v-show や display:none だとスクロール判定ができないので、 visibility を切り替える -->
  <!-- firstScrollFinished: テスト用に設定しておく -->
  <div
    v-show="filterdTalks.length"
    class="talkroom_messages scroll-y"
    :class="initRenderingFinished ? 'firstScrollFinished' : ''"
    :style="canShowMessages ? '' : 'visibility:hidden'"
  >
    <AtomsTalkMessageLoading :loading="showLoadingTalksTop" />
    <DynamicScroller
      ref="scrollArea"
      :items="filterdTalks"
      :min-item-size="54"
      class="scroller h-100"
      key-field="talkId"
      :emit-update="true"
      data-testid="talkroom-messages"
    >
      <template #before>
        <InfiniteLoading :identifier="identifierTop" direction="top" @infinite="onScrollTop">
          <template #spinner><div></div></template>
          <template #no-more><div></div></template>
          <template #no-results><div></div></template>
        </InfiniteLoading>
      </template>
      <template #default="{ item, index, active }">
        <!-- 最後尾にはpb入れてメッセージ誤送信を予防 -->
        <DynamicScrollerItem
          :class="item.talkId === lastActiveTalkId ? 'pb-8' : ''"
          :item="item"
          :active="active"
          :size-dependencies="[item.contents]"
          :data-index="index"
          :data-talkid="item.talkId"
          :watch-data="true"
        >
          <!-- 未読ライン -->
          <div v-if="canShowUnreadLine(item)" ref="unreadLineRef" class="talk-unread-line">NEW</div>

          <!-- トーク日付 -->
          <AtomsTalkDate v-if="item.messageType === 'date'" :label="item.date!" />
          <!-- トークメッセージ -->
          <OrganismsTalkMessage
            v-else
            :ref="el => setTalkMessageRef(item.talkId, el)"
            :key="item.talkId"
            :can-action="canAction"
            :plan-id="planId"
            :talk-room-id="talkRoomId"
            :talk="item"
            :can-like="canLike"
            :is-owner="isOwner"
            :is-owner-or-member="isOwnerOrMember"
            :image-talks="item.messageType === 'image' ? filteredImageTalks : []"
            :owner-ids="ownerIds"
            :user-good-list="talkRoomStore.userGoodList[talkRoomId] || []"
            :scroll="scrollToReply"
            :search-word="searchWord"
            @on-user-click="val => userProfileModal?.openModal(val?.userId)"
            @on-like-click="val => talkLikeDialog?.openModal(val.talkId, item.goodCount)"
          />
        </DynamicScrollerItem>
      </template>
      <template #after>
        <InfiniteLoading :identifier="identifierBottom" direction="bottom" @infinite="onScrollBottom">
          <template #spinner><div></div></template>
          <template #no-more><div></div></template>
          <template #no-results><div></div></template>
        </InfiniteLoading>
      </template>
    </DynamicScroller>
    <AtomsTalkMessageLoading :loading="showLoadingTalksBottom" />
    <!-- スクロール中に表示する未読件数 -->
    <Transition name="fade">
      <template v-if="canShowUnreadTalks && unreadTalks">
        <div class="unread-talks d-flex px-2" @click="clickUnreadTalks">
          <span class="ma-auto">{{ unreadTalks }}</span>
          <v-icon
            class="unread-talks-close ml-auto my-auto"
            size="14px"
            color="white"
            icon="$mdiClose"
            @click.stop="closeUnreadTalks"
          ></v-icon>
        </div>
      </template>
    </Transition>
    <!-- スクロール中に表示する日付 -->
    <Transition name="fade">
      <template v-if="isScrolling && talkDate">
        <div class="talkdate">{{ talkDate }}</div>
      </template>
    </Transition>
    <!-- スクロール中に表示する最新トークへのジャンプボタン -->
    <Transition name="fade">
      <AtomsScrollToBottomButton
        v-if="showJumpToLastTalk"
        class="jump-to-last-talk"
        data-testid="talkroom-jump-to-last-talk"
        @click="onJumpToLastTalk"
      />
    </Transition>

    <!-- トークスレッド用ダイアログ -->
    <OrganismsTalkMessageThread
      v-if="showThread"
      v-model:open="showThread"
      :plan-id="planId"
      :talk-room-id="talkRoomId"
      :thread-parent-talk-id="threadTalk!.talkId"
      :target-talk-id="threadTargetTalkId"
      :can-action="canAction"
      :can-like="canLike"
      :is-owner="isOwner"
      :is-owner-or-member="isOwnerOrMember"
      :owner-ids="ownerIds"
      :user-good-list="talkRoomStore.userGoodList[talkRoomId] || []"
      :is-test="isTest"
      @on-user-click="val => userProfileModal?.openModal(val?.userId)"
      @on-like-click="val => talkLikeDialog?.openModal(val.talkId, val.goodCount)"
    ></OrganismsTalkMessageThread>
  </div>

  <MoleculesUserProfileDialog
    ref="userProfileModal"
    :plan-id="planId"
    :plan-info="planInfo"
    :is-owner="isOwner"
    :show-dm="true"
    :skip-fetching-talk-rooms="true"
    from="talk-room"
  />
  <MoleculesTalkLikeDialog ref="talkLikeDialog" @on-user-click="val => userProfileModal?.openModal(val?.userId)" />
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";

  .talkroom_messages {
    height: v-bind("messageAreaHeight");
    padding: 20px;
    padding-top: 0px;
    padding-bottom: v-bind("inputAreaHeight");
    position: relative;
  }
  // スクロール中に表示するエフェクト
  .fade-leave-active {
    transition: opacity 0.5s ease;
  }
  .fade-leave-to {
    opacity: 0;
  }
  // スクロール中に表示する未読件数
  .unread-talks {
    position: absolute;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    -webkit-transform: translateX(-50%);
    -ms-transform: translateX(-50%);
    width: 120px;
    border-radius: 20px;
    background-color: #cb2e31;
    font-size: 11px;
    text-align: center;
    padding: 3px 0 5px;
    margin: 0 auto;
    z-index: 100;
    cursor: pointer;
  }
  // スクロール中に表示する日付
  .talkdate {
    position: absolute;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    -webkit-transform: translateX(-50%);
    -ms-transform: translateX(-50%);
    width: 67px;
    border-radius: 20px;
    background-color: #333333;
    font-size: 11px;
    text-align: center;
    padding: 3px 0 5px;
    margin: 0 auto;
  }
  // スクロール中に表示する最新トークへのジャンプボタン
  .jump-to-last-talk {
    position: absolute;
    bottom: v-bind("inputAreaHeight");
    right: 0;
    margin: 16px;
  }
  .infinite-status-prompt {
    display: none;
  }
  @include display_tablet {
    .talkroom_messages {
      padding: 15px;
      padding-top: 0px;
      padding-bottom: v-bind("inputAreaHeight");
      .vue-recycle-scroller {
        padding-bottom: 10px;
      }
    }
  }
  .vue-recycle-scroller {
    // スクロールバーを非表示にする (以下の一式でクロスブラウザ対応)
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
  .talk-unread-line {
    width: 100%;
    border-bottom: 1px solid #cb2e31;
    text-align: right;
    color: #cb2e31;
  }
</style>
